import React, { createContext, useContext, useReducer, useState, ReactNode, SetStateAction } from 'react';

// ----------------------------------------------------------------------

export interface ContractState {
  // Folders
  folders: Array<{ name: string; count: number }>;

  // File upload state
  selectedFile: File | null;
  filePreviewData: ArrayBuffer | null;
  isUploading: boolean;
  showConfirmation: boolean;

  // PDF preview state
  showPreview: boolean;
  pdfOverlayUrl: string | null;

  // Contract name editing state
  editingContractId: string | null;
  editingContractName: string;
  originalName: string;

  // Delete confirmation state
  showDeleteConfirmation: boolean;
  contractToDelete: string | null;
  contractNameToDelete: string;

  // Search and filtering state (filter params only — filtered results derived in hook)
  filterState: 'owned' | 'shared' | 'all';
  searchQuery: string;

  // Profile management for shared users
  profilePictures: Record<string, string>;
  userDisplayInfo: Record<string, { displayName: string; email: string }>;
}

export interface ContractStateActions {
  // Independent data setters
  setFolders: React.Dispatch<React.SetStateAction<Array<{ name: string; count: number }>>>;
  setProfilePictures: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setUserDisplayInfo: React.Dispatch<React.SetStateAction<Record<string, { displayName: string; email: string }>>>;
  setShowPreview: React.Dispatch<React.SetStateAction<boolean>>;
  setPdfOverlayUrl: React.Dispatch<React.SetStateAction<string | null>>;

  // Legacy setters (for backward compatibility)
  setSelectedFile: (file: File | null) => void;
  setIsUploading: (uploading: boolean) => void;
  setShowConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingContractName: React.Dispatch<React.SetStateAction<string>>;
  setFilterState: (filter: 'owned' | 'shared' | 'all') => void;
  setSearchQuery: (query: string) => void;

  // Action dispatchers (preferred API)
  dispatchEditing: React.Dispatch<EditingAction>;
  dispatchDelete: React.Dispatch<DeleteAction>;
  dispatchUpload: React.Dispatch<UploadAction>;
  dispatchSearch: React.Dispatch<SearchAction>;
}

// ----------------------------------------------------------------------
// REDUCER TYPES AND LOGIC

// 1. Contract Editing State (related actions that should be atomic)
interface EditingState {
  editingContractId: string | null;
  editingContractName: string;
  originalName: string;
}

export type EditingAction =
  | { type: 'START_EDITING'; payload: { id: string; name: string } }
  | { type: 'UPDATE_NAME'; payload: string }
  | { type: 'SAVE_SUCCESS' }
  | { type: 'CANCEL' };

const editingReducer = (state: EditingState, action: EditingAction): EditingState => {
  switch (action.type) {
    case 'START_EDITING':
      return {
        editingContractId: action.payload.id,
        editingContractName: action.payload.name,
        originalName: action.payload.name
      };
    case 'UPDATE_NAME':
      return { ...state, editingContractName: action.payload };
    case 'SAVE_SUCCESS':
    case 'CANCEL':
      return {
        editingContractId: null,
        editingContractName: "",
        originalName: ""
      };
    default:
      return state;
  }
};

// 2. Delete Confirmation State (related modal state)
interface DeleteState {
  showDeleteConfirmation: boolean;
  contractToDelete: string | null;
  contractNameToDelete: string;
}

export type DeleteAction =
  | { type: 'SHOW_CONFIRMATION'; payload: { id: string; name: string } }
  | { type: 'HIDE_CONFIRMATION' };

const deleteReducer = (state: DeleteState, action: DeleteAction): DeleteState => {
  switch (action.type) {
    case 'SHOW_CONFIRMATION':
      return {
        showDeleteConfirmation: true,
        contractToDelete: action.payload.id,
        contractNameToDelete: action.payload.name
      };
    case 'HIDE_CONFIRMATION':
      return {
        showDeleteConfirmation: false,
        contractToDelete: null,
        contractNameToDelete: ""
      };
    default:
      return state;
  }
};

// 3. File Upload State (multi-step upload process)
interface UploadState {
  selectedFile: File | null;
  filePreviewData: ArrayBuffer | null;
  isUploading: boolean;
  showConfirmation: boolean;
}

export type UploadAction =
  | { type: 'SELECT_FILE'; payload: { file: File; previewData: ArrayBuffer } }
  | { type: 'START_UPLOAD' }
  | { type: 'UPLOAD_SUCCESS' }
  | { type: 'UPLOAD_ERROR' }
  | { type: 'SHOW_CONFIRMATION' }
  | { type: 'HIDE_CONFIRMATION' }
  | { type: 'RESET' };

const uploadReducer = (state: UploadState, action: UploadAction): UploadState => {
  switch (action.type) {
    case 'SELECT_FILE':
      return {
        ...state,
        selectedFile: action.payload.file,
        filePreviewData: action.payload.previewData
      };
    case 'START_UPLOAD':
      return { ...state, isUploading: true };
    case 'UPLOAD_SUCCESS':
      return {
        selectedFile: null,
        filePreviewData: null,
        isUploading: false,
        showConfirmation: false
      };
    case 'UPLOAD_ERROR':
      return { ...state, isUploading: false };
    case 'SHOW_CONFIRMATION':
      return { ...state, showConfirmation: true };
    case 'HIDE_CONFIRMATION':
      return { ...state, showConfirmation: false };
    case 'RESET':
      return {
        selectedFile: null,
        filePreviewData: null,
        isUploading: false,
        showConfirmation: false
      };
    default:
      return state;
  }
};

// 4. Search State (filter params only)
interface SearchState {
  filterState: 'owned' | 'shared' | 'all';
  searchQuery: string;
}

export type SearchAction =
  | { type: 'SET_FILTER'; payload: 'owned' | 'shared' | 'all' }
  | { type: 'SET_QUERY'; payload: string }
  | { type: 'CLEAR_SEARCH' };

const searchReducer = (state: SearchState, action: SearchAction): SearchState => {
  switch (action.type) {
    case 'SET_FILTER':
      return { ...state, filterState: action.payload };
    case 'SET_QUERY':
      return { ...state, searchQuery: action.payload };
    case 'CLEAR_SEARCH':
      return { ...state, searchQuery: "" };
    default:
      return state;
  }
};

export type ContractStateContextType = ContractState & ContractStateActions;

const ContractStateContext = createContext<ContractStateContextType | undefined>(undefined);

// ----------------------------------------------------------------------

interface ContractStateProviderProps {
  children: ReactNode;
}

export const ContractStateProvider: React.FC<ContractStateProviderProps> = ({ children }) => {
  // Independent state
  const [folders, setFolders] = useState<Array<{ name: string; count: number }>>([
    { name: "Untitled Folder", count: 2 },
    { name: "Contract 2024", count: 4 },
    { name: "Contract 2023", count: 6 },
    { name: "Contract 2022", count: 8 },
  ]);
  const [profilePictures, setProfilePictures] = useState<Record<string, string>>({});
  const [userDisplayInfo, setUserDisplayInfo] = useState<Record<string, { displayName: string; email: string }>>({});

  // PDF preview state
  const [showPreview, setShowPreview] = useState(false);
  const [pdfOverlayUrl, setPdfOverlayUrl] = useState<string | null>(null);

  // Grouped related state with useReducer
  const [editingState, dispatchEditing] = useReducer(editingReducer, {
    editingContractId: null,
    editingContractName: "",
    originalName: ""
  });

  const [deleteState, dispatchDelete] = useReducer(deleteReducer, {
    showDeleteConfirmation: false,
    contractToDelete: null,
    contractNameToDelete: ""
  });

  const [uploadState, dispatchUpload] = useReducer(uploadReducer, {
    selectedFile: null,
    filePreviewData: null,
    isUploading: false,
    showConfirmation: false
  });

  const [searchState, dispatchSearch] = useReducer(searchReducer, {
    filterState: "owned",
    searchQuery: "",
  });

  const contextValue: ContractStateContextType = {
    // Independent state
    folders,
    profilePictures,
    userDisplayInfo,
    showPreview,
    pdfOverlayUrl,

    // Grouped state (flatten for backward compatibility)
    selectedFile: uploadState.selectedFile,
    filePreviewData: uploadState.filePreviewData,
    isUploading: uploadState.isUploading,
    showConfirmation: uploadState.showConfirmation,

    editingContractId: editingState.editingContractId,
    editingContractName: editingState.editingContractName,
    originalName: editingState.originalName,

    showDeleteConfirmation: deleteState.showDeleteConfirmation,
    contractToDelete: deleteState.contractToDelete,
    contractNameToDelete: deleteState.contractNameToDelete,

    filterState: searchState.filterState,
    searchQuery: searchState.searchQuery,

    // Independent actions
    setFolders,
    setProfilePictures,
    setUserDisplayInfo,
    setShowPreview,
    setPdfOverlayUrl,

    // Legacy actions (simplified for backward compatibility - encourage using dispatchers)
    setSelectedFile: (file: File | null) => {
      if (file) {
        dispatchUpload({ type: 'SELECT_FILE', payload: { file, previewData: new ArrayBuffer(0) } });
      } else {
        dispatchUpload({ type: 'RESET' });
      }
    },
    setIsUploading: (uploading: boolean) => {
      dispatchUpload({ type: uploading ? 'START_UPLOAD' : 'UPLOAD_ERROR' });
    },
    setShowConfirmation: (show: SetStateAction<boolean>) => {
      const newValue = typeof show === 'function' ? show(uploadState.showConfirmation) : show;
      dispatchUpload({ type: newValue ? 'SHOW_CONFIRMATION' : 'HIDE_CONFIRMATION' });
    },

    setEditingContractName: (name: SetStateAction<string>) => {
      const newValue = typeof name === 'function' ? name(editingState.editingContractName) : name;
      dispatchEditing({ type: 'UPDATE_NAME', payload: newValue });
    },

    setFilterState: (filter: 'owned' | 'shared' | 'all') => dispatchSearch({ type: 'SET_FILTER', payload: filter }),
    setSearchQuery: (query: string) => dispatchSearch({ type: 'SET_QUERY', payload: query }),

    // Action dispatchers (preferred API)
    dispatchEditing,
    dispatchDelete,
    dispatchUpload,
    dispatchSearch,
  };

  return (
    <ContractStateContext.Provider value={contextValue}>
      {children}
    </ContractStateContext.Provider>
  );
};

/**
 * Hook to access contract state - throws error if used outside provider
 */
const useContractState = (): ContractStateContextType => {
  const context = useContext(ContractStateContext);

  if (context === undefined) {
    throw new Error('useContractState must be used within a ContractStateProvider');
  }

  return context;
};

export default useContractState;
